import React from 'react';
import { Sparkles, Settings2, AlertCircle, Brain, CheckCircle } from 'lucide-react';
import ChampIcon from './shell/ChampIcon';
import SessionsAnalytics from './analytics/SessionsSection';
import SectionLabel from '../design-system/SectionLabel';

/**
 * APEX v8.13 — SessionTab
 *
 * Onglet "Sessions" du menu principal.
 *   - Bloc session en cours (KPIs, recommandation stop, etc.)
 *   - Liste des sessions précédentes
 *   - Analytics des sessions (graphes)
 *
 * Extrait de App.jsx v8.13 (était défini inline).
 */
export default function SessionTab({ games, sessions, onCloseSession, onSessionAIAnalyze, onManageSessions, aiAnalyses, champions, version }) {
  // games de la session en cours = games créées après la dernière session fermée
  const lastSessionDate = sessions.length > 0 ? new Date(sessions[0].closed_at) : new Date(0);
  // Utiliser timestamp (date réelle de la game) en priorité, fallback sur created_at
  const currentSessionGames = games.filter(g => {
    const gameDate = new Date(g.timestamp);
    return gameDate > lastSessionDate;
  });

  const recent = currentSessionGames.slice(-3);
  const losses = recent.filter(g => g.result === 'L').length;
  const shouldStop = losses >= 2;
  const avgFeeling = recent.length > 0 ? recent.reduce((a, b) => a + b.feeling, 0) / recent.length : 0;
  const lastMental = recent[recent.length - 1]?.mental || 'N/A';

  return (
    <div className="space-y-6">
      {/* Session en cours */}
      <div className="border border-zinc-800 p-4 md:p-5 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-400/5 rounded-full blur-3xl" />
        <div className="relative">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
            <div className="flex items-center gap-3">
              <Brain size={16} className="text-cyan-400 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase font-semibold">Session en Cours</div>
                <div className="text-xs text-zinc-400 font-mono mt-1">{currentSessionGames.length} game{currentSessionGames.length > 1 ? 's' : ''} · depuis {sessions.length > 0 ? new Date(sessions[0].closed_at).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'toujours'}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={onManageSessions}
                className="text-xs tracking-wider text-zinc-400 hover:text-cyan-400 border border-zinc-800 hover:border-cyan-400/50 px-3 py-2 transition flex items-center gap-2 font-semibold"
                title="Gérer les sessions"
              >
                <Settings2 size={12} /> GÉRER
              </button>
              {currentSessionGames.length > 0 && (
                <button
                  onClick={onCloseSession}
                  className="bg-gradient-to-r from-cyan-400 to-fuchsia-400 text-black px-4 py-2 font-black text-sm tracking-wider hover:opacity-90 transition flex items-center gap-2"
                >
                  <CheckCircle size={14} /> FERMER LA SESSION
                </button>
              )}
            </div>
          </div>

          {shouldStop && (
            <div className="border-2 border-rose-500 bg-rose-500/10 p-4 mb-4 flex items-start gap-3">
              <AlertCircle size={20} className="text-rose-400 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-black text-rose-400 tracking-wider mb-1">STOP RECOMMANDÉ</div>
                <div className="text-sm text-zinc-300">Tu es à {losses} défaites sur les 3 dernières games. Ta règle : stop pour la journée.</div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            {[0, 1, 2].map(i => {
              const g = recent[recent.length - 1 - i];
              return (
                <div key={i} className={`border p-3 ${g ? (g.result === 'W' ? 'border-cyan-400/30 bg-cyan-400/5' : 'border-fuchsia-500/30 bg-fuchsia-500/5') : 'border-zinc-800 border-dashed'}`}>
                  <div className="text-[10px] text-zinc-500 font-mono mb-1">GAME -{i}</div>
                  {g ? (
                    <div className="flex items-center gap-2">
                      <ChampIcon name={g.champion} champions={champions} version={version} size={32} />
                      <div className="min-w-0">
                        <div className={`font-black text-lg ${g.result === 'W' ? 'text-cyan-400' : 'text-fuchsia-400'}`}>{g.result}</div>
                        <div className="text-[10px] text-zinc-500 font-mono truncate">{g.kills}/{g.deaths}/{g.assists}</div>
                      </div>
                    </div>
                  ) : <div className="text-zinc-700">—</div>}
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-zinc-800">
            <div>
              <div className="text-[10px] text-zinc-500 tracking-wider uppercase">Feeling Moyen</div>
              <div className="text-2xl font-black bg-gradient-to-r from-cyan-400 to-fuchsia-400 bg-clip-text text-transparent">{avgFeeling.toFixed(1)}<span className="text-zinc-600 text-sm">/10</span></div>
            </div>
            <div>
              <div className="text-[10px] text-zinc-500 tracking-wider uppercase">Dernier Mental</div>
              <div className="text-xl font-black text-cyan-400">{lastMental}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Règles */}
      <div className="border border-zinc-800 p-5">
        <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase font-semibold mb-3">Règles de Discipline</div>
        <div className="grid grid-cols-2 gap-3 text-sm text-zinc-400">
          <div className="flex items-start gap-2"><div className="w-1.5 h-1.5 bg-cyan-400 mt-2 flex-shrink-0" /><div><span className="text-white font-semibold">Max 3 ranked/session</span> puis break 10min</div></div>
          <div className="flex items-start gap-2"><div className="w-1.5 h-1.5 bg-cyan-400 mt-2 flex-shrink-0" /><div><span className="text-white font-semibold">Lose streak à 2</span> = stop pour la journée</div></div>
          <div className="flex items-start gap-2"><div className="w-1.5 h-1.5 bg-fuchsia-400 mt-2 flex-shrink-0" /><div><span className="text-white font-semibold">Pas de ranked après 22h</span> en semaine</div></div>
          <div className="flex items-start gap-2"><div className="w-1.5 h-1.5 bg-fuchsia-400 mt-2 flex-shrink-0" /><div><span className="text-white font-semibold">ZÉRO role swap</span> pendant le bloc de 100 games</div></div>
        </div>
      </div>

      {/* Historique des sessions */}
      {sessions.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <SectionLabel>Sessions Précédentes</SectionLabel>
            <button
              onClick={onManageSessions}
              className="text-xs tracking-wider text-zinc-400 hover:text-cyan-400 border border-zinc-800 hover:border-cyan-400/50 px-3 py-1.5 transition flex items-center gap-2 font-semibold"
            >
              <Settings2 size={12} /> GÉRER
            </button>
          </div>
          <div className="space-y-3">
            {sessions.slice(0, 10).map(s => {
              const positive = s.wins > s.losses;
              const hasAnalysis = aiAnalyses?.some(a => a.session_id === s.id && a.response_text);
              return (
                <div key={s.id} className={`border p-4 ${positive ? 'border-cyan-400/20 bg-cyan-400/[0.02]' : 'border-fuchsia-500/20 bg-fuchsia-500/[0.02]'}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="text-xs text-zinc-400 font-mono">{new Date(s.closed_at).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })}</div>
                      <div className={`font-black text-2xl ${positive ? 'text-cyan-400' : 'text-fuchsia-400'}`}>
                        {s.wins}<span className="text-zinc-600 text-lg">-{s.losses}</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="text-right text-xs text-zinc-500 font-mono">
                        <div>{s.games_count} game{s.games_count > 1 ? 's' : ''}</div>
                        <div className="text-cyan-400">{s.mental_global}</div>
                      </div>
                      <button
                        onClick={() => onSessionAIAnalyze(s)}
                        className={`p-2 border transition ${hasAnalysis ? 'border-cyan-400/50 bg-cyan-400/10 text-cyan-400' : 'border-zinc-700 text-zinc-400 hover:border-cyan-400 hover:text-cyan-400'}`}
                        title={hasAnalysis ? 'Analyse Apex disponible' : 'Analyser avec Apex'}
                      >
                        <Sparkles size={12} />
                      </button>
                    </div>
                  </div>
                  {s.insights && (
                    <div className="text-sm text-zinc-400 italic border-l-2 border-zinc-700 pl-3 mt-2">
                      "{s.insights}"
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* V8.11 : Graphes analytics sessions (déplacé depuis Analytics > Sessions) */}
      {sessions.length > 0 && (
        <div>
          <SectionLabel>Analytics des sessions</SectionLabel>
          <SessionsAnalytics sessions={sessions} />
        </div>
      )}
    </div>
  );
}
